/**
 * X40, announced intent (the F.E.A.R. lesson; the owner liked it): a squad's current tactic — what it is doing now, not
 * what it was told — derived from the state only (no sim change, read by the table's tag, the ground view's and the order
 * log). Lane E's look (ui/commandIcons.ts `Intent`, its glyphs, its bark words and timing) draws it; this module chooses
 * the word, from the first source that says something, in this order:
 *
 *  1. riding — most of its living members sit in a body that drives;
 *  2. building — its order puts something up (a relay, the beacon);
 *  3. withdrawing — its order is to withdraw, or most of its members' plans retreat;
 *  4. a play (68.22) — the support suppresses; the assault bounds in;
 *  5. a flank order — flanking left or right;
 *  6. its leader's step (68.9) — a bound bounds, a hold holds (an approach says nothing of its own);
 *  7. its order — a bound bounds, a hold holds;
 *  8. its members' plans — most fighting: suppressing; most searching: scouting; most at their posts: holding; most
 *     hunting (advancing on the objective or the enemy — a skirmish's squads with no order, seen in play): bounding.
 *
 * `since`: the tick the source says it began — a task's issue, the leader's step, a ride's boarding — or absent where the
 * source has none (the members' plans: a bot re-plans every fraction of a second, so a plan's tick is not when the tactic
 * began — seen in play, every squad's tag barked without end). The drawing times the bark from the change it sees
 * (`observeIntent`). Pure.
 */
import type { GameState, MatchSquad, SquadOrder } from '../types';
import { squadOrderFor, squadTaskFor } from './squads';
import { drives } from './body';

/** The tactics a squad announces (the same words as lane E's `Intent`, ui/commandIcons.ts). */
export type SquadIntent = 'holding' | 'bounding' | 'flankingLeft' | 'flankingRight' | 'suppressing' | 'withdrawing' | 'scouting' | 'building' | 'riding';
export const SQUAD_INTENTS: readonly SquadIntent[] = ['holding', 'bounding', 'flankingLeft', 'flankingRight', 'suppressing', 'withdrawing', 'scouting', 'building', 'riding'];

type IntentState = Pick<GameState, 'players' | 'squads' | 'structures'> & Partial<Pick<GameState, 'plays' | 'actionBrains' | 'squadApart'>>;

/** A squad's current tactic and the tick it began, or undefined (nothing to announce: no member standing, or no source says). */
export function squadIntent(state: IntentState, sq: MatchSquad, tick: number): { intent: SquadIntent; since?: number } | undefined {
  const living = sq.unitIds.map(id => state.players.find(p => p.id === id)).filter((p): p is NonNullable<typeof p> => !!p && p.alive);
  if (!living.length) return undefined;
  const most = (n: number) => n * 2 > living.length;
  const brains = state.actionBrains ?? {};
  const lead = living[0].id, order: SquadOrder | null = squadOrderFor(state, lead, tick), task = squadTaskFor(state, lead, tick);
  const taskSince = task?.issuedTick ?? 0;
  // 1. riding
  const seated = living.filter(p => p.mount && state.structures.some(s => s.id === p.mount!.bodyId && drives(s)));
  if (most(seated.length)) return { intent: 'riding', since: Math.max(0, ...seated.map(p => brains[p.id]?.ride?.since ?? 0)) };
  // 2. building
  if (order && (order.kind === 'build' || order.kind === 'beacon')) return { intent: 'building', since: taskSince };
  // 3. withdrawing
  const plans = living.map(p => brains[p.id]?.plan ?? null);
  const retreating = plans.filter(pl => pl?.job === 'retreat');
  if (order?.kind === 'withdraw') return { intent: 'withdrawing', since: taskSince };
  if (most(retreating.length)) return { intent: 'withdrawing' };
  // 4. a play
  const play = (state.plays ?? []).find(pl => pl.phase !== 'done' && (pl.support.includes(sq.id) || pl.assault.includes(sq.id)));
  if (play) return { intent: play.support.includes(sq.id) ? 'suppressing' : 'bounding', since: taskSince };
  // 5. a flank
  if (order?.kind === 'flank') return { intent: order.side === 'left' ? 'flankingLeft' : 'flankingRight', since: taskSince };
  // 6. the leader's step
  const step = living.map(p => brains[p.id]?.lead).find(l => l !== undefined);
  if (step?.kind === 'bound') return { intent: 'bounding', since: step.since };
  if (step?.kind === 'hold') return { intent: 'holding', since: step.since };
  // 7. the order
  if (order?.kind === 'bound') return { intent: 'bounding', since: taskSince };
  if (order?.kind === 'hold') return { intent: 'holding', since: taskSince };
  // 8. the members' plans
  const by = (f: (pl: NonNullable<typeof plans[number]>) => boolean) => plans.filter((pl): pl is NonNullable<typeof pl> => !!pl && f(pl));
  const fighting = by(pl => pl.mode === 'fight' && pl.targetId !== null);
  if (most(fighting.length)) return { intent: 'suppressing' };
  const searching = by(pl => pl.mode === 'search' || pl.job === 'search' || pl.job === 'roam');
  if (most(searching.length)) return { intent: 'scouting' };
  const posted = by(pl => pl.atPost === true);
  if (most(posted.length)) return { intent: 'holding' };
  const hunting = by(pl => pl.mode === 'hunt');
  if (most(hunting.length)) return { intent: 'bounding' };
  return undefined;
}

/**
 * The drawing's bark clock: seconds since `id`'s word last changed as watched (`now` seconds), recording the change in
 * `seen`; −1 for a squad first seen (it does not bark) or one whose word has not changed since. `age`: the derivation's own
 * (a fresh order's), taken when it is newer than the change watched.
 */
export function observeIntent(seen: Map<number, { intent: SquadIntent; at: number }>, id: number, intent: SquadIntent, now: number, age?: number): number {
  const was = seen.get(id);
  if (!was) { seen.set(id, { intent, at: age !== undefined ? now - age : -Infinity }); return age ?? -1; }
  if (was.intent !== intent) { was.intent = intent; was.at = age !== undefined ? now - age : now; }
  return was.at === -Infinity ? -1 : now - was.at;
}
